/**
 * EG Lore Reader
 * --------------
 * Layers reading-experience polish onto the About card's full-screen
 * dialog: scroll progress (top bar + header meter), idle UI fade for
 * cinematic immersion, and a "back to top" affordance that appears once
 * the reader has scrolled past the first viewport.
 *
 * Open/close, focus trap, scroll lock and ESC are all handled by the
 * theme's <dialog-component> wrapper. This element only owns the
 * in-reader UX.
 */

class EgLoreReader extends HTMLElement {
  #initialized = false;
  #contentPrepared = false;
  #scroll;
  #article;
  #meterFill;
  #progressMeter;
  #progressLabel;
  #toTop;
  #header;
  #toc;
  #tocList;
  #chip;
  #chipLabel;
  #chipBackdrop;
  #idleTimer = null;
  #lastProgress = -1;
  #chapters = [];
  #chapterObserver = null;
  #activeChapterId = null;
  #suppressObserverUntil = 0;
  #chipOpen = false;

  static IDLE_MS = 2200;

  connectedCallback() {
    if (this.#initialized) return;
    this.#initialized = true;

    this.#scroll = this.querySelector('[data-reader-scroll]');
    this.#article = this.querySelector('[data-reader-article]');
    this.#meterFill = this.querySelector('[data-reader-meter-fill]');
    this.#progressMeter = this.querySelector('[data-reader-progress-meter]');
    this.#progressLabel = this.querySelector('[data-reader-progress-label]');
    this.#toTop = this.querySelector('[data-reader-to-top]');
    this.#header = this.querySelector('[data-reader-header]');
    this.#toc = this.querySelector('[data-reader-toc]');
    this.#tocList = this.querySelector('[data-reader-toc-list]');
    this.#chip = this.querySelector('[data-reader-chip]');
    this.#chipLabel = this.querySelector('[data-reader-chip-label]');
    this.#chipBackdrop = this.querySelector('[data-reader-chip-backdrop]');

    if (!this.#scroll) return;

    this.#scroll.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#scroll.addEventListener('mousemove', this.#wakeUI, { passive: true });
    this.#scroll.addEventListener('touchstart', this.#wakeUI, { passive: true });
    this.#scroll.addEventListener('keydown', this.#wakeUI);

    if (this.#toTop) {
      this.#toTop.addEventListener('click', this.#scrollToTop);
    }

    // The dialog wrapper opens/closes through <dialog-component>. Reset
    // scroll + progress every time the reader appears so a re-open doesn't
    // start mid-scroll from a previous read.
    const dialogComp = this.closest('dialog-component');
    if (dialogComp) {
      dialogComp.addEventListener('dialog:open', this.#onDialogOpen);
      dialogComp.addEventListener('dialog:close', this.#onDialogClose);
      if (dialogComp.querySelector('dialog')?.open) this.#onDialogOpen();
    }
  }

  disconnectedCallback() {
    this.#scroll?.removeEventListener('scroll', this.#onScroll);
    this.#scroll?.removeEventListener('mousemove', this.#wakeUI);
    this.#scroll?.removeEventListener('touchstart', this.#wakeUI);
    this.#scroll?.removeEventListener('keydown', this.#wakeUI);
    this.#toTop?.removeEventListener('click', this.#scrollToTop);
    this.#tocList?.removeEventListener('click', this.#onTocClick);
    this.#chip?.removeEventListener('click', this.#toggleChip);
    this.#chipBackdrop?.removeEventListener('click', this.#closeChip);
    document.removeEventListener('keydown', this.#onKeydown);
    this.#chapterObserver?.disconnect();
    this.#chapterObserver = null;
    if (this.#idleTimer) clearTimeout(this.#idleTimer);
  }

  #onDialogOpen = () => {
    if (!this.#contentPrepared) {
      this.#contentPrepared = true;
      this.#hydrateInlineImages();
      this.#buildChapters();
    }
    // Reset to the top — re-opening the same character should always
    // start from the beginning of the story.
    if (this.#scroll) {
      this.#scroll.scrollTop = 0;
    }
    this.#updateProgress(0);
    if (this.#chapters.length > 0) {
      this.#setActiveChapter(this.#chapters[0].id);
    }
    this.#wakeUI();
    // Move keyboard focus into the reader content so screen readers
    // announce the title and arrow keys scroll the article. Defer to
    // the next tick so the dialog has finished rendering.
    setTimeout(() => {
      this.#scroll?.focus({ preventScroll: true });
    }, 0);
  };

  #onDialogClose = () => {
    this.classList.remove('eg-lore-reader__shell--idle');
    this.#closeChip();
    if (this.#idleTimer) {
      clearTimeout(this.#idleTimer);
      this.#idleTimer = null;
    }
  };

  #onScroll = () => {
    const el = this.#scroll;
    if (!el) return;
    const max = Math.max(1, el.scrollHeight - el.clientHeight);
    const progress = Math.min(1, Math.max(0, el.scrollTop / max));
    this.#updateProgress(progress);

    if (this.#toTop) {
      const showToTop = el.scrollTop > el.clientHeight * 0.6;
      if (showToTop && this.#toTop.hasAttribute('hidden')) {
        this.#toTop.removeAttribute('hidden');
      } else if (!showToTop && !this.#toTop.hasAttribute('hidden')) {
        this.#toTop.setAttribute('hidden', '');
      }
    }

    this.#wakeUI();
  };

  #updateProgress(progress) {
    const pct = Math.round(progress * 100);
    if (pct === this.#lastProgress) return;
    this.#lastProgress = pct;

    this.style.setProperty('--eg-reader-progress', String(progress));

    if (this.#meterFill) {
      this.#meterFill.style.transform = `scaleX(${progress})`;
    }
    if (this.#progressMeter) {
      this.#progressMeter.setAttribute('aria-valuenow', String(pct));
    }
    if (this.#progressLabel) {
      this.#progressLabel.textContent = `${pct}%`;
    }
  }

  #wakeUI = () => {
    this.classList.remove('eg-lore-reader__shell--idle');
    if (this.#idleTimer) clearTimeout(this.#idleTimer);
    this.#idleTimer = window.setTimeout(() => {
      // Only dim chrome when the user has actually started reading.
      if (this.#scroll && this.#scroll.scrollTop > 80) {
        this.classList.add('eg-lore-reader__shell--idle');
      }
    }, EgLoreReader.IDLE_MS);
  };

  #scrollToTop = () => {
    if (!this.#scroll) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#scroll.scrollTo({
      top: 0,
      behavior: reduced ? 'auto' : 'smooth',
    });
  };

  /**
   * Scan the rich-text body for headings (h2/h3) and build a clickable
   * chapter list in the side panel. Each heading gets a stable id so the
   * TOC links can scroll-anchor to it. IntersectionObserver keeps the
   * active item in sync with the reader's scroll position. If no headings
   * exist, the TOC stays hidden.
   */
  #buildChapters() {
    if (!this.#toc || !this.#tocList) return;
    // Free chapters and (with a partial gate) the locked title stubs live in
    // separate bodies; both feed the chapter list.
    const bodies = Array.from(this.querySelectorAll('.eg-lore-reader__body'));
    if (bodies.length === 0) return;

    const headings = bodies.flatMap((body) => Array.from(body.querySelectorAll('h2, h3')));
    if (headings.length === 0) return;

    this.#chapters = [];
    const usedIds = new Set();
    const frag = document.createDocumentFragment();

    headings.forEach((h, idx) => {
      const text = (h.textContent || '').trim();
      if (!text) return;

      if (!h.id) h.id = this.#makeChapterId(text, idx, usedIds);
      else usedIds.add(h.id);

      // Give the heading a scroll margin so it lands below the header bar
      // rather than being hidden behind it.
      h.classList.add('eg-lore-reader__chapter-anchor');

      const li = document.createElement('li');
      li.className = `eg-lore-reader__toc-item eg-lore-reader__toc-item--${h.tagName.toLowerCase()}`;

      const a = document.createElement('a');
      a.className = 'eg-lore-reader__toc-link';
      a.href = `#${h.id}`;
      a.dataset.chapterId = h.id;
      a.textContent = text;
      if (h.closest('[data-reader-locked]')) a.dataset.locked = 'true';
      li.appendChild(a);
      frag.appendChild(li);

      this.#chapters.push({ id: h.id, el: h, level: h.tagName });
    });

    if (this.#chapters.length === 0) return;

    this.#tocList.replaceChildren(frag);
    this.#toc.removeAttribute('hidden');
    this.#toc.dataset.hasChapters = 'true';
    this.classList.add('eg-lore-reader__shell--has-toc');

    // Reveal the mobile chip — CSS still keeps it hidden on desktop where
    // the sidebar TOC is visible.
    if (this.#chip) {
      this.#chip.removeAttribute('hidden');
      this.#chip.addEventListener('click', this.#toggleChip);
    }
    this.#chipBackdrop?.addEventListener('click', this.#closeChip);
    document.addEventListener('keydown', this.#onKeydown);

    this.#tocList.addEventListener('click', this.#onTocClick);

    // Watch chapter headings; the topmost intersecting one wins. The
    // negative bottom margin biases activation toward whatever sits near
    // the top of the viewport so the highlight moves as the user reads.
    this.#chapterObserver = new IntersectionObserver(this.#onChapterIntersect, {
      root: this.#scroll,
      rootMargin: '-15% 0px -70% 0px',
      threshold: [0, 1],
    });
    this.#chapters.forEach(({ el }) => this.#chapterObserver.observe(el));

    this.#setActiveChapter(this.#chapters[0].id);
  }

  #makeChapterId(text, idx, usedIds) {
    const slug = text
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60);
    let base = slug || `chapter-${idx + 1}`;
    let id = `eg-lore-ch-${base}`;
    let n = 2;
    while (usedIds.has(id) || document.getElementById(id)) {
      id = `eg-lore-ch-${base}-${n++}`;
    }
    usedIds.add(id);
    return id;
  }

  #onTocClick = (event) => {
    const link = event.target.closest('a[data-chapter-id]');
    if (!link) return;
    event.preventDefault();
    const id = link.dataset.chapterId;
    const target = this.#chapters.find((c) => c.id === id)?.el;
    if (!target || !this.#scroll) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Compute target position relative to the scroll container, leaving
    // headroom for the sticky header fade.
    const top = target.offsetTop - this.#headerOffset();
    // Suppress observer-driven active changes while the click-scroll is
    // animating — without this, the observer fires mid-flight and the
    // active state flips to whatever heading the smooth-scroll passes
    // through (often a sub-heading immediately below the target).
    this.#suppressObserverUntil = performance.now() + (reduced ? 0 : 800);
    this.#scroll.scrollTo({
      top: Math.max(0, top),
      behavior: reduced ? 'auto' : 'smooth',
    });
    this.#setActiveChapter(id);
    // Picking a chapter from the mobile sheet should dismiss the sheet so
    // the user can read what they just selected.
    this.#closeChip();
    this.#wakeUI();
  };

  #onChapterIntersect = (entries) => {
    // Ignore observer pulses during a TOC-click-initiated scroll so the
    // clicked chapter stays highlighted instead of being overridden by a
    // sub-heading that flashes through the activation band.
    if (performance.now() < this.#suppressObserverUntil) return;
    // Pick the entry currently closest to the top of the viewport that is
    // still intersecting; fall back to the last one above the viewport if
    // none intersect (so the active stays meaningful at the end of a section).
    const intersecting = entries.filter((e) => e.isIntersecting);
    if (intersecting.length === 0) return;
    intersecting.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
    const id = intersecting[0].target.id;
    if (id) this.#setActiveChapter(id);
  };

  #setActiveChapter(id) {
    if (id === this.#activeChapterId) return;
    this.#activeChapterId = id;
    if (!this.#tocList) return;
    const links = this.#tocList.querySelectorAll('a[data-chapter-id]');
    let activeText = '';
    links.forEach((a) => {
      const isActive = a.dataset.chapterId === id;
      a.classList.toggle('eg-lore-reader__toc-link--active', isActive);
      if (isActive) {
        a.setAttribute('aria-current', 'true');
        activeText = a.textContent || '';
        // Keep the active item in view in the TOC panel itself.
        const li = a.parentElement;
        if (li && typeof li.scrollIntoView === 'function') {
          li.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      } else {
        a.removeAttribute('aria-current');
      }
    });
    if (this.#chipLabel) {
      this.#chipLabel.textContent = activeText;
    }
  }

  #headerOffset() {
    const h = this.#header?.getBoundingClientRect().height ?? 0;
    return h + 16;
  }

  #toggleChip = () => {
    if (this.#chipOpen) this.#closeChip();
    else this.#openChip();
  };

  #openChip = () => {
    if (this.#chipOpen) return;
    this.#chipOpen = true;
    this.classList.add('eg-lore-reader__shell--toc-open');
    this.#chip?.setAttribute('aria-expanded', 'true');
    // Bring the current chapter into view so a tap reveals the surrounding
    // entries rather than starting at the top of the list every time.
    const active = this.#tocList?.querySelector('.eg-lore-reader__toc-link--active');
    active?.parentElement?.scrollIntoView({ block: 'nearest' });
  };

  #closeChip = () => {
    if (!this.#chipOpen) return;
    this.#chipOpen = false;
    this.classList.remove('eg-lore-reader__shell--toc-open');
    this.#chip?.setAttribute('aria-expanded', 'false');
  };

  #onKeydown = (event) => {
    if (event.key === 'Escape' && this.#chipOpen) {
      event.stopPropagation();
      this.#closeChip();
      this.#chip?.focus();
    }
  };

  /**
   * Walk paragraphs in the body looking for ones whose text content is an
   * `<img …>` tag (the rich-text editor doesn't allow real img tags, so
   * authors type them as text and they survive as escaped textContent).
   * Replace each match with a real `<figure><img></figure>`. Validates the
   * src protocol so this can't introduce javascript: / data:text/html sinks.
   */
  #hydrateInlineImages() {
    const body = this.querySelector('.eg-lore-reader__body');
    if (!body) return;

    // `<img …>` or `<img … />`. Be liberal about quoting and whitespace.
    const tagRe = /^<img\b([^>]*?)\/?>$/i;

    const paragraphs = Array.from(body.querySelectorAll('p'));
    for (const p of paragraphs) {
      const text = p.textContent.trim();
      if (!text || text.length > 2000) continue;
      const match = text.match(tagRe);
      if (!match) continue;

      const attrs = this.#parseAttrs(match[1]);
      const src = attrs.src;
      if (!src || !this.#isSafeImageUrl(src)) continue;

      const fig = document.createElement('figure');
      fig.className = 'eg-lore-reader__figure';

      const img = document.createElement('img');
      img.alt = attrs.alt || '';
      img.loading = 'lazy';
      img.decoding = 'async';
      img.src = src;
      if (attrs.width && /^\d+$/.test(attrs.width)) img.width = Number(attrs.width);
      if (attrs.height && /^\d+$/.test(attrs.height)) img.height = Number(attrs.height);
      fig.appendChild(img);

      const captionText = attrs.caption || attrs.title;
      if (captionText) {
        const cap = document.createElement('figcaption');
        cap.className = 'eg-lore-reader__figure-caption';
        cap.textContent = captionText;
        fig.appendChild(cap);
      }

      p.replaceWith(fig);
    }
  }

  /**
   * Parse a string of HTML attributes (`src="…" alt="…"`) into an object.
   * Tolerates double quotes, single quotes, and unquoted values.
   */
  #parseAttrs(str) {
    const attrs = {};
    const re = /([a-zA-Z_:][\w:.-]*)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/g;
    let m;
    while ((m = re.exec(str)) !== null) {
      attrs[m[1].toLowerCase()] = (m[2] ?? m[3] ?? m[4] ?? '').trim();
    }
    return attrs;
  }

  /**
   * Allow only http(s) URLs and `data:image/*` payloads. Rejects javascript:,
   * vbscript:, data:text/html, etc. so a content author who types a malicious
   * URL into the rich text can't escalate it into script execution.
   */
  #isSafeImageUrl(url) {
    if (typeof url !== 'string' || url.length === 0) return false;
    if (/^data:image\/(?:png|jpe?g|gif|webp|svg\+xml|avif)[;,]/i.test(url)) {
      // Disallow inline scripts in SVG payloads — easiest to be conservative.
      return !/^data:image\/svg\+xml/i.test(url) || !/[<%](?:script|on\w+=)/i.test(url);
    }
    try {
      // Resolve protocol-relative and relative URLs against current origin.
      const resolved = new URL(url, window.location.href);
      return resolved.protocol === 'http:' || resolved.protocol === 'https:';
    } catch {
      return false;
    }
  }
}

if (!window.customElements.get('eg-lore-reader')) {
  window.customElements.define('eg-lore-reader', EgLoreReader);
}
